import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Calendar } from "@/components/ui/calendar";
import { DayContent, type DayContentProps } from "react-day-picker";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Flame, Moon, Brain, Heart, Activity, Droplets, 
  Plus, TrendingUp, ArrowLeft, Home, X, LogOut,
  Coffee, Wine, UtensilsCrossed, Dumbbell, Users, 
  CloudRain, ThermometerSun, Pill, Tag, ChevronDown,
  Award, Sparkles, Calendar as CalendarIcon, Zap, Lightbulb
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Link, useNavigate } from "react-router-dom";
import { DoctorReport } from "@/components/DoctorReport";
import { BladderDiary } from "@/components/BladderDiary";
import { PeriodTracker } from "@/components/PeriodTracker";
import { FluidIntakeChart } from "@/components/FluidIntakeChart";
import { HydrationGoalTracker } from "@/components/HydrationGoalTracker";
import { BladderDiaryCalendarView } from "@/components/BladderDiaryCalendarView";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSymptomTracker } from "@/hooks/useSymptomTracker";
import { TimePicker } from "@/components/ui/time-picker";
import womanJournaling from "@/assets/woman-journaling-tracking.jpg";

const sleepQualityIcon = "/sleep-quality-icon.png";
const hotFlashesIcon = "/hot-flashes-icon.png";
const brainFogIcon = "/brain-fog-icon.png";
const moodIcon = "/mood-icon.png";
const howAreYouFeelingIcon = "/how-are-you-feeling-icon.png";
const energyLevelsIcon = "/energy-levels-icon.png";
const hydrationIcon = "/hydration-icon.png";

const symptoms = [
  { id: "hotflash", label: "Hot Flashes", icon: Flame, image: hotFlashesIcon, color: "text-accent" },
  { id: "sleep", label: "Sleep Quality", icon: Moon, image: sleepQualityIcon, color: "text-primary" },
  { id: "brainfog", label: "Brain Fog", icon: Brain, image: brainFogIcon, color: "text-accent" },
  { id: "mood", label: "Mood", icon: Heart, image: moodIcon, color: "text-primary" },
  { id: "energy", label: "Energy Level", icon: Activity, image: energyLevelsIcon, color: "text-accent" },
  { id: "hydration", label: "Hydration", icon: Droplets, image: hydrationIcon, color: "text-primary" },
];

const renderSymptomIcon = (symptom: typeof symptoms[0], className?: string) => {
  if (symptom.image) {
    return <img src={symptom.image} alt={symptom.label} className={`object-cover rounded-full ${className}`} />;
  }
  const Icon = symptom.icon;
  return <Icon className={className} />;
};

const formatCalendarKey = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const triggerOptions = [
  { id: "stress", label: "Stress", icon: Brain },
  { id: "caffeine", label: "Caffeine", icon: Coffee },
  { id: "alcohol", label: "Alcohol", icon: Wine },
  { id: "spicy_food", label: "Spicy Food", icon: UtensilsCrossed },
  { id: "exercise", label: "Exercise", icon: Dumbbell },
  { id: "social", label: "Social Event", icon: Users },
  { id: "weather", label: "Weather Change", icon: CloudRain },
  { id: "heat", label: "Hot Environment", icon: ThermometerSun },
  { id: "medication", label: "Medication", icon: Pill },
];

const Tracker = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [newHotFlashTime, setNewHotFlashTime] = useState("");
  const [showTimeInput, setShowTimeInput] = useState(false);
  const [customEpisodeTime, setCustomEpisodeTime] = useState<Record<string, string>>({});
  const [bladderDiaryData, setBladderDiaryData] = useState<Array<{
    date: string;
    intakeEntries: Array<{ time: string; amount: number; type: string }>;
    urinationEntries: Array<{ time: string; urgency: number; hadLeakage: boolean; leakageAmount?: string; notes?: string }>;
  }>>([]);
  const [hydrationGoal, setHydrationGoal] = useState(2000);
  const [episodeCustomTriggers, setEpisodeCustomTriggers] = useState<Record<string, string>>({});
  const [expandedEpisodes, setExpandedEpisodes] = useState<Record<string, boolean>>({});
  const [sleepLogs, setSleepLogs] = useState<any[]>([]);
  const [periodLogs, setPeriodLogs] = useState<Array<{ id: string; log_date: string; flow_intensity: string; notes: string | null }>>([]);
  const [monthSymptomLogs, setMonthSymptomLogs] = useState<Record<string, Array<{ id: string | null; symptom_type: string; intensity: number | null; created_at: string | null }>>>({});
  const [monthDailyNotes, setMonthDailyNotes] = useState<Record<string, string>>({});
  const [monthHotFlashTimes, setMonthHotFlashTimes] = useState<Record<string, string[]>>({});
  const [monthDailyTriggers, setMonthDailyTriggers] = useState<Record<string, string[]>>({});
  const [monthSleepSummaries, setMonthSleepSummaries] = useState<Record<string, Array<{ sleep_quality: number | null; times_woken: number | null; notes: string | null }>>>({});

  const { 
    symptomEpisodes,
    addSymptomEpisode,
    deleteSymptomEpisode,
    hotFlashTimes, 
    setHotFlashTimes, 
    dailyNote, 
    setDailyNote,
    dailyTriggers,
    setDailyTriggers,
    symptomTriggers,
    addSymptomTrigger,
    removeSymptomTrigger,
    clearAllTriggers,
    loading,
    saving,
    saveTrackerData,
    datesWithLogs
  } = useSymptomTracker(date || new Date());

  // Slider values for each symptom
  const [sliderValues, setSliderValues] = useState<Record<string, number>>({
    hotflash: 5,
    sleep: 5,
    brainfog: 5,
    mood: 5,
    energy: 5,
    hydration: 5
  });

  // Fetch bladder diary data for the last 30 days
  useEffect(() => {
    const fetchBladderDiaryData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const { data, error } = await supabase
          .from('bladder_diary')
          .select('*')
          .eq('user_id', user.id)
          .gte('log_date', thirtyDaysAgo.toISOString().split('T')[0])
          .order('log_date', { ascending: true })
          .order('log_time', { ascending: true });

        if (error) throw error;

        // Group entries by date
        const groupedData: Record<string, {
          intakeEntries: Array<{ time: string; amount: number; type: string }>;
          urinationEntries: Array<{ time: string; urgency: number; hadLeakage: boolean; leakageAmount?: string; notes?: string }>;
        }> = {};

        data?.forEach((entry: any) => {
          const dateStr = new Date(entry.log_date).toLocaleDateString();
          
          if (!groupedData[dateStr]) {
            groupedData[dateStr] = {
              intakeEntries: [],
              urinationEntries: []
            };
          }

          if (entry.entry_type === 'intake') {
            groupedData[dateStr].intakeEntries.push({
              time: entry.log_time,
              amount: entry.fluid_amount_ml || 0,
              type: entry.fluid_type || ''
            });
          } else if (entry.entry_type === 'urination') {
            groupedData[dateStr].urinationEntries.push({
              time: entry.log_time,
              urgency: entry.urgency_level || 0,
              hadLeakage: entry.had_leakage || false,
              leakageAmount: entry.leakage_amount || undefined,
              notes: entry.notes || undefined
            });
          }
        });

        const formattedData = Object.entries(groupedData).map(([date, entries]) => ({
          date,
          ...entries
        }));

        setBladderDiaryData(formattedData);
      } catch (error) {
        console.error('Error fetching bladder diary data:', error);
      }
    };

    fetchBladderDiaryData();
  }, []);

  // Fetch hydration goal from profile
  useEffect(() => {
    const fetchHydrationGoal = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await supabase
          .from('profiles')
          .select('hydration_goal')
          .eq('user_id', user.id)
          .single();

        if (error) throw error;
        if (data?.hydration_goal) {
          setHydrationGoal(data.hydration_goal);
        }
      } catch (error) {
        console.error('Error fetching hydration goal:', error);
      }
    };

    fetchHydrationGoal();
  }, []);

  // Fetch sleep logs for calendar view
  useEffect(() => {
    const fetchSleepLogs = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const { data, error } = await supabase
          .from('sleep_logs')
          .select('*')
          .eq('user_id', user.id)
          .gte('log_date', thirtyDaysAgo.toISOString().split('T')[0])
          .order('log_date', { ascending: false });

        if (error) throw error;
        setSleepLogs(data || []);
      } catch (error) {
        console.error('Error fetching sleep logs:', error);
      }
    };

    fetchSleepLogs();
  }, []);

  // Fetch period logs for doctor report
  useEffect(() => {
    const fetchPeriodLogs = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const { data, error } = await supabase
          .from('period_logs')
          .select('*')
          .eq('user_id', user.id)
          .gte('log_date', thirtyDaysAgo.toISOString().split('T')[0])
          .order('log_date', { ascending: false });

        if (error) throw error;
        setPeriodLogs(data || []);
      } catch (error) {
        console.error('Error fetching period logs:', error);
      }
    };

    fetchPeriodLogs();
  }, []);

  // Fetch symptom logs, daily notes, and hot flashes for the visible calendar month (for hover tooltips)
  useEffect(() => {
    const fetchMonthData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
        const startStr = formatCalendarKey(start);
        const endStr = formatCalendarKey(end);

        const [
          { data: logs, error: logsError },
          { data: notes, error: notesError },
          { data: hf, error: hotFlashesError },
          { data: triggers, error: triggersError },
          { data: sleep, error: sleepError },
        ] = await Promise.all([
          supabase.from('symptom_logs').select('id, log_date, symptom_type, intensity, created_at')
            .eq('user_id', user.id).gte('log_date', startStr).lte('log_date', endStr),
          supabase.from('daily_notes').select('log_date, notes')
            .eq('user_id', user.id).gte('log_date', startStr).lte('log_date', endStr),
          supabase.from('hot_flash_times').select('log_date, time_of_day')
            .eq('user_id', user.id).gte('log_date', startStr).lte('log_date', endStr),
          supabase.from('daily_triggers').select('log_date, trigger_type')
            .eq('user_id', user.id).gte('log_date', startStr).lte('log_date', endStr),
          supabase.from('sleep_logs').select('log_date, sleep_quality, times_woken, notes')
            .eq('user_id', user.id).gte('log_date', startStr).lte('log_date', endStr),
        ]);

        if (logsError || notesError || hotFlashesError || triggersError || sleepError) {
          throw logsError || notesError || hotFlashesError || triggersError || sleepError;
        }

        const grouped: Record<string, Array<{ id: string | null; symptom_type: string; intensity: number | null; created_at: string | null }>> = {};
        (logs || []).forEach((l: any) => {
          if (!grouped[l.log_date]) grouped[l.log_date] = [];
          grouped[l.log_date].push({ id: l.id, symptom_type: l.symptom_type, intensity: l.intensity, created_at: l.created_at });
        });
        setMonthSymptomLogs(grouped);

        const noteMap: Record<string, string> = {};
        (notes || []).forEach((n: any) => { if (n.notes) noteMap[n.log_date] = n.notes; });
        setMonthDailyNotes(noteMap);

        const hfMap: Record<string, string[]> = {};
        (hf || []).forEach((h: any) => {
          if (!hfMap[h.log_date]) hfMap[h.log_date] = [];
          if (h.time_of_day) hfMap[h.log_date].push(h.time_of_day);
        });
        setMonthHotFlashTimes(hfMap);

        const triggerMap: Record<string, string[]> = {};
        (triggers || []).forEach((t: any) => {
          if (!triggerMap[t.log_date]) triggerMap[t.log_date] = [];
          if (t.trigger_type && !triggerMap[t.log_date].includes(t.trigger_type)) {
            triggerMap[t.log_date].push(t.trigger_type);
          }
        });
        setMonthDailyTriggers(triggerMap);

        const sleepMap: Record<string, Array<{ sleep_quality: number | null; times_woken: number | null; notes: string | null }>> = {};
        (sleep || []).forEach((s: any) => {
          if (!sleepMap[s.log_date]) sleepMap[s.log_date] = [];
          sleepMap[s.log_date].push({
            sleep_quality: s.sleep_quality,
            times_woken: s.times_woken,
            notes: s.notes,
          });
        });
        setMonthSleepSummaries(sleepMap);
      } catch (err) {
        console.error('Error fetching month calendar data:', err);
      }
    };
    fetchMonthData();
  }, [currentMonth, saving]);


  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Logged out",
        description: "You've been successfully logged out.",
      });
      
      navigate('/auth');
    } catch (error: any) {
      toast({
        title: "Error logging out",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Calculate today's water intake from bladder diary
  const getTodayWaterIntake = () => {
    const today = date?.toLocaleDateString() || new Date().toLocaleDateString();
    const todayData = bladderDiaryData.find(d => d.date === today);
    return todayData?.intakeEntries.reduce((sum, entry) => sum + entry.amount, 0) || 0;
  };

  // Quick add water intake
  const handleQuickAddWater = async (amount: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const now = new Date();
      const logDate = date || now;
      const logTime = now.toTimeString().slice(0, 5);

      await supabase
        .from('bladder_diary')
        .insert({
          user_id: user.id,
          log_date: logDate.toISOString().split('T')[0],
          log_time: logTime,
          entry_type: 'intake',
          fluid_amount_ml: amount,
          fluid_type: 'water'
        });

      toast({
        title: "Water logged!",
        description: `Added ${amount}ml to your hydration tracker.`,
      });

      // Refresh bladder diary data
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('bladder_diary')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', thirtyDaysAgo.toISOString().split('T')[0])
        .order('log_date', { ascending: true })
        .order('log_time', { ascending: true });

      if (!error && data) {
        const groupedData: Record<string, {
          intakeEntries: Array<{ time: string; amount: number; type: string }>;
          urinationEntries: Array<{ time: string; urgency: number; hadLeakage: boolean; leakageAmount?: string; notes?: string }>;
        }> = {};

        data?.forEach((entry: any) => {
          const dateStr = new Date(entry.log_date).toLocaleDateString();
          
          if (!groupedData[dateStr]) {
            groupedData[dateStr] = {
              intakeEntries: [],
              urinationEntries: []
            };
          }

          if (entry.entry_type === 'intake') {
            groupedData[dateStr].intakeEntries.push({
              time: entry.log_time,
              amount: entry.fluid_amount_ml || 0,
              type: entry.fluid_type || ''
            });
          } else if (entry.entry_type === 'urination') {
            groupedData[dateStr].urinationEntries.push({
              time: entry.log_time,
              urgency: entry.urgency_level || 0,
              hadLeakage: entry.had_leakage || false,
              leakageAmount: entry.leakage_amount || undefined,
              notes: entry.notes || undefined
            });
          }
        });

        const formattedData = Object.entries(groupedData).map(([date, entries]) => ({
          date,
          ...entries
        }));

        setBladderDiaryData(formattedData);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to log water intake.",
        variant: "destructive",
      });
    }
  };

  // Update hydration goal
  const handleUpdateHydrationGoal = async (newGoal: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('profiles')
        .update({ hydration_goal: newGoal })
        .eq('user_id', user.id);

      if (error) throw error;

      setHydrationGoal(newGoal);
      toast({
        title: "Goal updated!",
        description: `Your daily hydration goal is now ${newGoal}ml.`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to update hydration goal.",
        variant: "destructive",
      });
    }
  };

  // Removed - symptoms now tracked as individual episodes

  const addHotFlashTime = () => {
    if (newHotFlashTime.trim()) {
      setHotFlashTimes([...hotFlashTimes, newHotFlashTime.trim()]);
      setNewHotFlashTime("");
      setShowTimeInput(false);
    }
  };

  const removeHotFlashTime = (index: number) => {
    setHotFlashTimes(hotFlashTimes.filter((_, i) => i !== index));
  };

  const getIntensityLabel = (value: number, symptomId?: string) => {
    if (value === 0) return "Not tracked";
    
    switch (symptomId) {
      case "sleep":
        if (value <= 2) return "Good";
        if (value <= 5) return "Fair";
        if (value <= 7) return "Restless";
        return "Poor";
      
      case "hotflash":
        if (value <= 2) return "Mild";
        if (value <= 5) return "Moderate";
        if (value <= 7) return "Frequent";
        return "Intense";
      
      case "brainfog":
        if (value <= 2) return "Clear";
        if (value <= 5) return "Slight";
        if (value <= 7) return "Foggy";
        return "Very Foggy";
      
      case "mood":
        if (value <= 2) return "Good";
        if (value <= 5) return "Okay";
        if (value <= 7) return "Irritable";
        return "Struggling";
      
      case "energy":
        if (value <= 2) return "Energized";
        if (value <= 5) return "Good";
        if (value <= 7) return "Low";
        return "Exhausted";
      
      case "hydration":
        if (value <= 2) return "Well-hydrated";
        if (value <= 5) return "Good";
        if (value <= 7) return "Low";
        return "Dehydrated";
      
      default:
        if (value <= 2) return "Mild";
        if (value <= 4) return "Moderate";
        if (value <= 7) return "Significant";
        return "Severe";
    }
  };

  const getIntensityColor = (value: number, symptomId?: string) => {
    if (value === 0) return "text-muted-foreground";
    
    // All symptoms use consistent thresholds matching labels (2, 5, 7)
    if (value <= 2) return "text-green-600 dark:text-green-400";
    if (value <= 5) return "text-yellow-600 dark:text-yellow-400";
    if (value <= 7) return "text-orange-600 dark:text-orange-400";
    return "text-red-600 dark:text-red-400";
  };

  const calendarLogDates = new Set(datesWithLogs);
  Object.keys(monthSymptomLogs).forEach((logDate) => calendarLogDates.add(logDate));
  Object.keys(monthDailyNotes).forEach((logDate) => calendarLogDates.add(logDate));
  Object.keys(monthHotFlashTimes).forEach((logDate) => calendarLogDates.add(logDate));
  Object.keys(monthDailyTriggers).forEach((logDate) => calendarLogDates.add(logDate));
  Object.keys(monthSleepSummaries).forEach((logDate) => calendarLogDates.add(logDate));
  periodLogs.forEach((log) => calendarLogDates.add(log.log_date));
  bladderDiaryData.forEach((entry) => {
    const parsedDate = new Date(entry.date);
    if (!Number.isNaN(parsedDate.getTime())) {
      calendarLogDates.add(formatCalendarKey(parsedDate));
    }
  });

  return (
    <div className="min-h-screen bg-background relative">
      {/* Background Image with Fade */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${womanJournaling})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/90 to-background/95" />
      </div>

      {/* Content */}
      <div className="relative z-10">
      {/* Ultra Modern Header with Glass Effect */}
      <div className="relative bg-gradient-to-r from-primary via-primary/90 to-accent text-white shadow-glow overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE0YzMuMzE0IDAgNiAyLjY4NiA2IDZzLTIuNjg2IDYtNiA2LTYtMi42ODYtNi02IDIuNjg2LTYgNi02ek0yNCA0NGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>
        <div className="container mx-auto px-4 py-8 relative z-10">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="text-white hover:bg-white/20 hover:scale-110 transition-all duration-300 backdrop-blur-sm"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="text-white hover:bg-white/20 hover:scale-110 transition-all duration-300 backdrop-blur-sm"
              >
                <Home className="h-5 w-5" />
              </Button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white hover:bg-white/20 gap-2 transition-all duration-300 backdrop-blur-sm hover:scale-105"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="animate-fade-in">
              <p className="text-[11px] uppercase tracking-[0.35em] text-white/70 mb-3">Her Season — Daily</p>
              <h1 className="text-4xl sm:text-6xl font-normal mb-3 leading-[1.05]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Your daily <em className="italic font-light">check-in</em>
              </h1>
              <p className="text-white/85 text-base sm:text-lg italic font-light max-w-xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                A quiet moment to notice how today is really going.
              </p>
            </div>
            <Link to="/sos-toolkit" className="animate-fade-in">
              <Button variant="default" className="bg-destructive/90 text-white hover:bg-destructive backdrop-blur-sm transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-semibold border-2 border-white/30">
                Need Help Now?
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="today" className="space-y-8">
          <TabsList className="grid w-full max-w-lg mx-auto grid-cols-3 bg-card/80 backdrop-blur-xl p-1.5 h-14 shadow-elegant rounded-2xl border border-primary/20">
            <TabsTrigger 
              value="today"
              className="rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/90 data-[state=active]:text-white data-[state=active]:shadow-glow transition-all duration-300 data-[state=active]:scale-105 font-medium text-xs sm:text-sm gap-2"
            >
              <Activity className="h-4 w-4" /> Today
            </TabsTrigger>
            <TabsTrigger 
              value="period"
              className="rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-pink-600 data-[state=active]:text-white data-[state=active]:shadow-glow transition-all duration-300 data-[state=active]:scale-105 font-medium text-xs sm:text-sm gap-2"
            >
              <Droplets className="h-4 w-4" /> Period
            </TabsTrigger>
            <TabsTrigger 
              value="calendar"
              className="rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/90 data-[state=active]:text-white data-[state=active]:shadow-glow transition-all duration-300 data-[state=active]:scale-105 font-medium text-xs sm:text-sm gap-2"
            >
              <CalendarIcon className="h-4 w-4" /> Calendar
            </TabsTrigger>
          </TabsList>

          <TabsContent value="today" className="space-y-6 animate-fade-in">
            {/* Date Display Card - Ultra Modern */}
            <Card className="p-8 bg-gradient-to-br from-white via-primary/5 to-accent/10 dark:from-card dark:via-primary/10 dark:to-accent/10 border-2 border-primary/20 shadow-glow hover:shadow-xl hover:border-primary/40 transition-all duration-300 rounded-3xl overflow-hidden relative group">
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 relative z-10">
                <div className="space-y-3">
                  <p className="text-[11px] uppercase tracking-[0.35em] text-primary/80">Today's Dateline</p>
                  <h2 className="text-3xl sm:text-4xl font-normal text-foreground leading-tight animate-fade-in" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {date?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </h2>
                  <p className="text-muted-foreground text-base italic flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    <img
                      src={howAreYouFeelingIcon}
                      alt=""
                      className="h-7 w-7 rounded-full object-cover shrink-0"
                      aria-hidden="true"
                    />
                    How are you feeling today?
                  </p>
                </div>
              </div>
            </Card>

            {/* Sleep Tracker Feature Card */}
            <Card className="p-6 bg-gradient-to-br from-primary/10 via-primary/5 to-background border-2 border-primary/30 shadow-lifted hover:shadow-glow hover:border-primary/50 transition-all duration-300 rounded-2xl">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                    <img src="/track-sleep-icon.png" alt="Track Your Sleep" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.35em] text-primary/80 mb-1.5">A Chapter On Rest</p>
                    <h3 className="text-2xl font-normal mb-1 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Track your sleep</h3>
                    <p className="text-sm text-muted-foreground italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      Quiet insights on what's disrupting your rest.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/sleep-tracker')}
                  className="bg-primary hover:bg-primary/90 gap-2"
                >
                  <Zap className="h-4 w-4" />
                  Start Tracking
                </Button>
              </div>
            </Card>

            {/* Symptom Tracking */}
            <div className="grid gap-6">
            {symptoms.map((symptom) => {
                const episodes = symptomEpisodes[symptom.id] || [];
                const sliderValue = sliderValues[symptom.id];
                const isHotFlash = symptom.id === "hotflash";
                
                return (
                  <Card key={symptom.id} className="p-6 hover:shadow-soft transition-shadow">
                    <div className="flex items-start gap-4">
                      {symptom.image ? (
                        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-primary-soft">
                          {renderSymptomIcon(symptom, "w-full h-full")}
                        </div>
                      ) : (
                        <div className={`p-3 rounded-full bg-primary-soft ${symptom.color}`}>
                          {renderSymptomIcon(symptom, "h-6 w-6")}
                        </div>
                      )}
                      <div className="flex-1 space-y-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.35em] text-primary/70 mb-1.5">A Note On</p>
                            <h3 className="text-2xl font-normal text-foreground leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                              {symptom.label}
                            </h3>
                            {symptom.id !== "sleep" && symptom.id !== "mood" && symptom.id !== "energy" && symptom.id !== "hydration" && (
                              <p className="text-sm text-muted-foreground italic mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                                {episodes.length} {episodes.length === 1 ? 'episode' : 'episodes'} today
                              </p>
                            )}
                            <p className="text-sm text-muted-foreground/80 italic mt-2 leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                              {symptom.id === "hydration"
                                ? "Slide to record your overall hydration for the day."
                                : "Slide to mark today's first episode — it saves as you go."}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-3">
                            <div className="relative">
                              <Slider
                                value={[sliderValue]}
                                onValueChange={(val) => setSliderValues(prev => ({ ...prev, [symptom.id]: val[0] }))}
                                max={10}
                                step={1}
                                className="w-full"
                                showValue={true}
                              />
                            </div>
                            <div className="flex justify-between text-xs text-muted-foreground">
                              {symptom.id === "sleep" ? (
                                <>
                                  <span>Good</span>
                                  <span>Fair</span>
                                  <span>Restless</span>
                                  <span>Poor</span>
                                </>
                              ) : symptom.id === "hotflash" ? (
                                <>
                                  <span>None</span>
                                  <span>Mild</span>
                                  <span>Moderate</span>
                                  <span>Intense</span>
                                </>
                              ) : symptom.id === "brainfog" ? (
                                <>
                                  <span>Clear</span>
                                  <span>Slight</span>
                                  <span>Foggy</span>
                                  <span>Very Foggy</span>
                                </>
                              ) : symptom.id === "mood" ? (
                                <>
                                  <span>Good</span>
                                  <span>Okay</span>
                                  <span>Irritable</span>
                                  <span>Struggling</span>
                                </>
                              ) : symptom.id === "energy" ? (
                                <>
                                  <span>Energized</span>
                                  <span>Good</span>
                                  <span>Low</span>
                                  <span>Exhausted</span>
                                </>
                              ) : symptom.id === "hydration" ? (
                                <>
                                  <span>Well-hydrated</span>
                                  <span>Good</span>
                                  <span>Low</span>
                                  <span>Dehydrated</span>
                                </>
                              ) : (
                                <>
                                  <span>None</span>
                                  <span>Mild</span>
                                  <span>Moderate</span>
                                  <span>Severe</span>
                                </>
                              )}
                            </div>
                            {/* Optional custom time input - hidden for hydration */}
                            {symptom.id !== "hydration" && (
                              <>
                                <div className="space-y-1">
                                  <label className="text-xs text-muted-foreground">Time (optional):</label>
                                  <TimePicker
                                    value={customEpisodeTime[symptom.id]}
                                    onChange={(time) => setCustomEpisodeTime(prev => ({ ...prev, [symptom.id]: time }))}
                                  />
                                </div>

                                 {/* Quick Add Mode Toggle */}
                                 <Button
                                  onClick={() => {
                                    const customTime = customEpisodeTime[symptom.id];
                                    addSymptomEpisode(symptom.id, sliderValue, customTime || undefined);
                                    setCustomEpisodeTime(prev => ({ ...prev, [symptom.id]: '' }));
                                    // Auto-expand episodes after adding
                                    setExpandedEpisodes(prev => ({ ...prev, [symptom.id]: true }));
                                  }}
                                  className="w-full"
                                  size="sm"
                                  variant="default"
                                >
                                  <Plus className="w-4 h-4 mr-2" />
                                  Add Episode
                                </Button>

                                <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1">
                                  <Lightbulb className="h-3.5 w-3.5" />
                                  Use this button to add any extra episodes throughout the day (select the time then click add episode)
                                </p>
                              </>
                            )}

                             {/* Display logged episodes with trigger dropdowns - Hide for hydration */}
                            {symptom.id !== "hydration" && episodes.length > 0 && (
                              <div className="space-y-2 pt-4 mt-4 border-t border-muted/50">
                                {/* Toggle Button - Collapsed when there are episodes */}
                                {!expandedEpisodes[symptom.id] && (
                                  <div className="space-y-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setExpandedEpisodes(prev => ({ ...prev, [symptom.id]: true }))}
                                      className="w-full justify-between hover:bg-primary/5 -mx-1 px-1"
                                    >
                                      <span className="text-xs font-semibold text-muted-foreground">
                                        Today's Episodes ({episodes.length})
                                      </span>
                                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    </Button>
                                    <p className="text-[10px] text-muted-foreground/70 px-1 flex items-center gap-1">
                                      <Lightbulb className="h-3 w-3" />
                                      Click to view episodes and add triggers (optional)
                                    </p>
                                  </div>
                                )}
                                
                                {/* Expanded Episodes Header */}
                                {expandedEpisodes[symptom.id] && (
                                  <div className="space-y-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setExpandedEpisodes(prev => ({ ...prev, [symptom.id]: false }))}
                                      className="w-full justify-between hover:bg-primary/5 -mx-1 px-1"
                                    >
                                      <span className="text-xs font-semibold text-muted-foreground">
                                        Today's Episodes ({episodes.length})
                                      </span>
                                      <ChevronDown className="h-4 w-4 text-muted-foreground rotate-180" />
                                    </Button>
                                    <div className="mt-2 p-2 bg-accent/10 rounded-lg border border-accent/20">
                                      <p className="text-[10px] text-muted-foreground/90 flex items-start gap-1.5">
                                        <span className="text-accent mt-0.5">⏰</span>
                                        <span>
                                          <strong className="text-accent">Why log triggers now?</strong> Tracking triggers in real-time helps you spot patterns and identify what truly affects your symptoms.
                                        </span>
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Episodes List - Always visible when expanded */}
                                {expandedEpisodes[symptom.id] && (
                                  <div className="space-y-2 animate-accordion-down">
                                    {episodes.map((episode) => {
                                      const episodeTriggers = symptomTriggers.get(episode.id || '') || [];
                                      const episodeId = episode.id || '';
                                      
                                      return (
                                        <div key={episode.id} className="p-3 bg-background/30 rounded-lg border border-muted space-y-2">
                                          <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                              {symptom.id !== "hydration" && (
                                                <span className="text-xs text-muted-foreground">{episode.time}</span>
                                              )}
                                              <span className={`text-xs font-medium ${getIntensityColor(episode.intensity, symptom.id)}`}>
                                                {getIntensityLabel(episode.intensity, symptom.id)}
                                              </span>
                                              <span className="text-sm font-bold text-primary">{episode.intensity}/10</span>
                                            </div>
                                            
                                            {/* Trigger Dropdown - Hide for hydration */}
                                            {symptom.id !== "hydration" && (
                                              <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 px-2 hover:bg-primary/10"
                                                  >
                                                    <Tag className="h-3.5 w-3.5 mr-1" />
                                                    <span className="text-xs">Trigger</span>
                                                    <ChevronDown className="h-3 w-3 ml-1" />
                                                  </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent className="w-56 bg-background border-border z-50" align="end">
                                                  <div className="p-2 space-y-1">
                                                    {triggerOptions.map((trigger) => {
                                                      const TriggerIcon = trigger.icon;
                                                      const isActive = episodeTriggers.includes(trigger.id);
                                                      return (
                                                        <DropdownMenuItem
                                                          key={trigger.id}
                                                          onClick={() => {
                                                            if (episode.id) {
                                                              if (isActive) {
                                                                removeSymptomTrigger(episode.id, trigger.id);
                                                              } else {
                                                                addSymptomTrigger(episode.id, trigger.id);
                                                              }
                                                            }
                                                          }}
                                                          className={`flex items-center gap-2 cursor-pointer ${
                                                            isActive ? 'bg-primary/20 text-primary' : ''
                                                          }`}
                                                        >
                                                          <TriggerIcon className="h-4 w-4" />
                                                          <span>{trigger.label}</span>
                                                          {isActive && <span className="ml-auto text-xs">✓</span>}
                                                        </DropdownMenuItem>
                                                      );
                                                    })}
                                                    <DropdownMenuSeparator />
                                                    <div className="p-2 space-y-2">
                                                      <Input
                                                        placeholder="Custom trigger..."
                                                        value={episodeCustomTriggers[episodeId] || ""}
                                                        onChange={(e) => setEpisodeCustomTriggers(prev => ({ ...prev, [episodeId]: e.target.value }))}
                                                        className="h-8 text-xs"
                                                        onKeyDown={(e) => {
                                                          if (e.key === 'Enter' && episodeCustomTriggers[episodeId]?.trim() && episode.id) {
                                                            const customId = `custom_${episodeCustomTriggers[episodeId].trim().toLowerCase().replace(/\s+/g, '_')}`;
                                                            addSymptomTrigger(episode.id, customId);
                                                            setEpisodeCustomTriggers(prev => ({ ...prev, [episodeId]: "" }));
                                                          }
                                                        }}
                                                      />
                                                      <Button
                                                        size="sm"
                                                        className="w-full h-7 text-xs"
                                                        onClick={() => {
                                                          if (episodeCustomTriggers[episodeId]?.trim() && episode.id) {
                                                            const customId = `custom_${episodeCustomTriggers[episodeId].trim().toLowerCase().replace(/\s+/g, '_')}`;
                                                            addSymptomTrigger(episode.id, customId);
                                                            setEpisodeCustomTriggers(prev => ({ ...prev, [episodeId]: "" }));
                                                          }
                                                        }}
                                                      >
                                                        Add Custom
                                                      </Button>
                                                    </div>
                                                  </div>
                                                </DropdownMenuContent>
                                              </DropdownMenu>
                                            )}
                                          </div>
                                          
                                          {/* Display active triggers */}
                                          {symptom.id !== "hydration" && episodeTriggers.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 pt-2 border-t border-muted/30">
                                              {episodeTriggers.map((triggerId) => {
                                                const trigger = triggerOptions.find(t => t.id === triggerId);
                                                const isCustom = triggerId.startsWith('custom_');
                                                const TriggerIcon = trigger?.icon || Tag;
                                                const label = trigger?.label || (isCustom ? triggerId.replace('custom_', '').replace(/_/g, ' ').charAt(0).toUpperCase() + triggerId.replace('custom_', '').replace(/_/g, ' ').slice(1) : triggerId);
                                                
                                                return (
                                                  <button
                                                    key={triggerId}
                                                    onClick={() => episode.id && removeSymptomTrigger(episode.id, triggerId)}
                                                    className="flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded-md text-xs hover:bg-primary/20 transition-colors"
                                                  >
                                                    <TriggerIcon className="h-3 w-3" />
                                                    <span>{label}</span>
                                                    <X className="h-3 w-3 ml-0.5 opacity-60 hover:opacity-100" />
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Daily Notes - Editorial */}
            <Card className="p-8 bg-card border border-border/60 rounded-2xl shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-border/60">
                  <img src="/daily-notes-icon.png" alt="" className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">A page from your day</p>
                  <h3 className="font-serif text-2xl leading-tight">Daily <em className="italic font-normal">notes</em></h3>
                </div>
              </div>
              <Textarea
                placeholder="How are you feeling today? Anything you noticed — a mood, a trigger, a small win."
                value={dailyNote}
                onChange={(e) => setDailyNote(e.target.value)}
                className="min-h-[140px] resize-none bg-background/50 border border-border/60 focus:border-primary/40 rounded-xl text-base font-serif leading-relaxed placeholder:italic placeholder:text-muted-foreground/70"
              />
              <div className="flex items-center justify-between mt-4 gap-4 flex-wrap">
                <p className="text-xs italic text-muted-foreground">
                  These notes will be included in your doctor report.
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      if (confirm('Are you sure you want to clear all triggers for today? This cannot be undone.')) {
                        clearAllTriggers();
                      }
                    }}
                    size="sm"
                    variant="ghost"
                    className="text-xs uppercase tracking-[0.15em] hover:bg-destructive/10 hover:text-destructive"
                  >
                    Clear triggers
                  </Button>
                  <Button
                    onClick={saveTrackerData}
                    disabled={saving}
                    size="sm"
                    variant="outline"
                    className="text-xs uppercase tracking-[0.15em]"
                  >
                    {saving ? "Saving…" : "Save notes"}
                  </Button>
                </div>
              </div>
            </Card>

            {/* Hydration Goal Tracker */}
            <HydrationGoalTracker 
              currentIntake={getTodayWaterIntake()}
              dailyGoal={hydrationGoal}
              onGoalUpdate={handleUpdateHydrationGoal}
            />

            {/* Bladder Diary */}
            <BladderDiary />
          </TabsContent>

          <TabsContent value="period" className="space-y-6 animate-fade-in">
            <PeriodTracker />
          </TabsContent>

          <TabsContent value="calendar" className="space-y-6 animate-fade-in">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Calendar Selection */}
              <Card className="p-8 bg-gradient-to-br from-white via-primary/5 to-accent/10 dark:from-card dark:via-primary/10 dark:to-accent/10 border-2 border-primary/20 shadow-elegant rounded-3xl">
                <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Your Symptom Calendar
                </h2>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => d && setDate(d)}
                  required
                  className="rounded-xl overflow-visible pointer-events-auto"
                  month={currentMonth}
                  onMonthChange={setCurrentMonth}
                  modifiers={{
                    hasLogs: Array.from(calendarLogDates).map(dateStr => new Date(dateStr + 'T00:00:00'))
                  }}
                  modifiersClassNames={{
                    hasLogs: "bg-primary/10 font-bold relative after:content-[''] after:absolute after:bottom-1.5 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:bg-primary after:rounded-full"
                  }}
                  components={{
                    DayContent: (dayProps: DayContentProps) => {
                      const d = dayProps.date;
                      const key = formatCalendarKey(d);
                      const isLoadedSelectedDay = date ? key === formatCalendarKey(date) : false;
                      const selectedDayLogs = Object.values(symptomEpisodes)
                        .flat()
                        .map((episode) => ({
                          id: episode.id || null,
                          symptom_type: episode.symptomType,
                          intensity: episode.intensity,
                          created_at: null,
                          display_time: episode.time,
                        }));
                      const logs = isLoadedSelectedDay && selectedDayLogs.length > 0 ? selectedDayLogs : monthSymptomLogs[key] || [];
                      const note = isLoadedSelectedDay && dailyNote ? dailyNote : monthDailyNotes[key];
                      const hotFlashEntries = isLoadedSelectedDay && hotFlashTimes.length > 0 ? hotFlashTimes : monthHotFlashTimes[key] || [];
                      const triggers = isLoadedSelectedDay && dailyTriggers.length > 0 ? dailyTriggers : monthDailyTriggers[key] || [];
                      const sleepEntries = monthSleepSummaries[key] || [];
                      const periodEntries = periodLogs.filter((log) => log.log_date === key);
                      const bladderEntry = bladderDiaryData.find((entry) => entry.date === d.toLocaleDateString());
                      const fluidTotal = bladderEntry?.intakeEntries.reduce((sum, entry) => sum + entry.amount, 0) || 0;
                      const bathroomTrips = bladderEntry?.urinationEntries.length || 0;
                      const hasBladderEntry = fluidTotal > 0 || bathroomTrips > 0;
                      const hasAny = logs.length > 0 || !!note || hotFlashEntries.length > 0 || triggers.length > 0 || sleepEntries.length > 0 || periodEntries.length > 0 || hasBladderEntry;
                      const isToday = key === formatCalendarKey(new Date());
                      if (!hasAny && !isToday) return <DayContent {...dayProps} />;
                      const symptomLabel = (t: string) =>
                        symptoms.find(s => s.id === t)?.label || t.replace(/_/g, ' ');
                      const triggerLabel = (t: string) =>
                        triggerOptions.find(option => option.id === t)?.label || t.replace(/_/g, ' ');
                      const formatSavedTime = (value?: string | null) => {
                        if (!value) return '';
                        if (value.includes('AM') || value.includes('PM')) return value;
                        const parsed = new Date(value);
                        if (!Number.isNaN(parsed.getTime())) {
                          return parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                        }
                        const [hours, minutes] = value.split(':');
                        const hourNumber = Number(hours);
                        if (Number.isNaN(hourNumber) || !minutes) return value;
                        const ampm = hourNumber >= 12 ? 'PM' : 'AM';
                        const displayHour = hourNumber % 12 || 12;
                        return `${displayHour}:${minutes} ${ampm}`;
                      };
                      return (
                        <span className="group relative flex h-full w-full items-center justify-center cursor-pointer overflow-visible">
                          <DayContent {...dayProps} />
                          <span className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 hidden w-72 -translate-x-1/2 rounded-md border border-border bg-popover p-3 text-left text-popover-foreground shadow-elegant group-hover:block group-focus-within:block">
                            <span className="block text-xs font-semibold text-foreground mb-2">
                              {d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                            </span>
                            {logs.length > 0 && (
                              <span className="block space-y-1 mb-2">
                                {logs.map((l, i) => (
                                  <span key={`${l.id || l.symptom_type}-${i}`} className="flex items-center justify-between gap-3 text-xs">
                                    <span className="min-w-0 text-foreground">
                                      <span className="capitalize">{symptomLabel(l.symptom_type)}</span>
                                      {'display_time' in l && l.display_time && (
                                        <span className="ml-1 text-muted-foreground">{l.display_time}</span>
                                      )}
                                      {!('display_time' in l) && l.created_at && (
                                        <span className="ml-1 text-muted-foreground">{formatSavedTime(l.created_at)}</span>
                                      )}
                                    </span>
                                    {l.intensity != null && (
                                      <span className="shrink-0 text-muted-foreground">{l.intensity}/10</span>
                                    )}
                                  </span>
                                ))}
                              </span>
                            )}
                            {hotFlashEntries.length > 0 && (
                              <span className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Flame className="h-3.5 w-3.5 text-accent" />
                                <span>{hotFlashEntries.length} hot flash{hotFlashEntries.length === 1 ? '' : 'es'} {hotFlashEntries.map(formatSavedTime).filter(Boolean).join(', ')}</span>
                              </span>
                            )}
                            {sleepEntries.length > 0 && (
                              <span className="mb-1 block text-xs text-muted-foreground">
                                Sleep: {sleepEntries.map((entry) => {
                                  const parts = [];
                                  if (entry.sleep_quality != null) parts.push(`quality ${entry.sleep_quality}/5`);
                                  if (entry.times_woken != null) parts.push(`${entry.times_woken} wake-up${entry.times_woken === 1 ? '' : 's'}`);
                                  return parts.join(', ');
                                }).filter(Boolean).join(' • ')}
                              </span>
                            )}
                            {periodEntries.length > 0 && (
                              <span className="mb-1 block text-xs text-muted-foreground">
                                Period: {periodEntries.map((entry) => entry.flow_intensity).join(', ')}
                              </span>
                            )}
                            {hasBladderEntry && (
                              <span className="mb-1 block text-xs text-muted-foreground">
                                Bladder diary: {fluidTotal > 0 ? `${fluidTotal}ml fluid` : ''}{fluidTotal > 0 && bathroomTrips > 0 ? ', ' : ''}{bathroomTrips > 0 ? `${bathroomTrips} bathroom trip${bathroomTrips === 1 ? '' : 's'}` : ''}
                              </span>
                            )}
                            {triggers.length > 0 && (
                              <span className="mt-2 block border-t border-border/50 pt-2 text-xs text-muted-foreground">
                                Triggers: {triggers.map(triggerLabel).join(', ')}
                              </span>
                            )}
                            {note && (
                              <span className="text-xs italic text-muted-foreground border-t border-border/50 pt-2 mt-2 line-clamp-3">
                                "{note}"
                              </span>
                            )}
                            {!hasAny && isToday && (
                              <span className="block text-xs text-muted-foreground">
                                Nothing saved for today yet.
                              </span>
                            )}
                          </span>
                        </span>
                      );
                    }
                  }}
                />
                
                {/* Legend and Stats */}
                <div className="mt-6 space-y-4">
                  {/* Tracking Stats */}
                  <div className="p-4 bg-gradient-to-br from-accent/10 to-primary/10 rounded-xl border border-accent/20">
                    <h3 className="text-sm font-semibold mb-3 text-foreground flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-accent" />
                      Your Tracking Progress
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="p-3 bg-background/60 rounded-lg">
                        <p className="text-xl font-bold text-primary">
                          {Array.from(datesWithLogs).filter(d => {
                            const logDate = new Date(d);
                            return (
                              logDate.getMonth() === currentMonth.getMonth() &&
                              logDate.getFullYear() === currentMonth.getFullYear()
                            );
                          }).length}
                        </p>
                        <p className="text-[9px] text-muted-foreground mt-0.5">This Month</p>
                      </div>
                      <div className="p-3 bg-background/60 rounded-lg">
                        <p className="text-xl font-bold text-accent">
                          {Array.from(datesWithLogs).filter(d => {
                            const logDate = new Date(d);
                            const today = new Date();
                            const diffTime = today.getTime() - logDate.getTime();
                            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                            return diffDays >= 0 && diffDays <= 7;
                          }).length}
                        </p>
                        <p className="text-[9px] text-muted-foreground mt-0.5">Last 7 Days</p>
                      </div>
                      <div className="p-3 bg-background/60 rounded-lg">
                        <p className="text-xl font-bold text-foreground">{datesWithLogs.size}</p>
                        <p className="text-[9px] text-muted-foreground mt-0.5">All Time</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground/70 mt-3 flex items-center gap-1">
                      <span>💡</span>
                      Consistent tracking helps identify patterns and triggers
                    </p>
                  </div>

                  {/* Monthly Insights */}
                  <div className="p-4 bg-gradient-to-br from-accent/10 via-background/50 to-primary/10 rounded-xl border border-accent/20">
                    <h3 className="text-sm font-semibold mb-3 text-foreground flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-accent" />
                      Monthly Insights
                    </h3>
                    <div className="space-y-2">
                      {(() => {
                        const thisMonthDates = Array.from(datesWithLogs).filter(d => {
                          const logDate = new Date(d);
                          return (
                            logDate.getMonth() === currentMonth.getMonth() &&
                            logDate.getFullYear() === currentMonth.getFullYear()
                          );
                        });
                        
                        if (thisMonthDates.length === 0) {
                          return (
                            <p className="text-xs text-muted-foreground italic">
                              Start tracking this month to see personalized insights!
                            </p>
                          );
                        }

                        const insights = [];
                        
                        // Most active dates
                        const dateCounts: Record<string, number> = {};
                        thisMonthDates.forEach(dateStr => {
                          // Count symptoms for each date (not implemented here, just tracking that the date has logs)
                          dateCounts[dateStr] = (dateCounts[dateStr] || 0) + 1;
                        });
                        
                        // Sort dates by most tracked and get top dates
                        const sortedDates = Object.entries(dateCounts)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 3);
                        
                        if (sortedDates.length > 0) {
                          const topDatesFormatted = sortedDates.map(([dateStr]) => {
                            const date = new Date(dateStr);
                            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                          }).join(', ');
                          insights.push(`Most active tracking: ${topDatesFormatted}`);
                        }

                        // Tracking consistency
                        const lastWeekCount = Array.from(datesWithLogs).filter(d => {
                          const logDate = new Date(d);
                          const today = new Date();
                          const diffTime = today.getTime() - logDate.getTime();
                          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                          return diffDays >= 0 && diffDays <= 7;
                        }).length;
                        if (lastWeekCount >= 5) {
                          insights.push("Great consistency this week!");
                        }

                        return insights.map((insight, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-xs text-foreground">
                            <Zap className="h-3 w-3 text-accent mt-0.5 flex-shrink-0" />
                            <span>{insight}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Month Comparison */}
                  <div className="p-4 bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl border border-border/50">
                    <h3 className="text-sm font-semibold mb-3 text-foreground flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4 text-primary" />
                      Year at a Glance
                    </h3>
                    {(() => {
                      const currentYear = currentMonth.getFullYear();
                      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      
                      const monthData = monthNames.map((name, index) => {
                        const count = Array.from(datesWithLogs).filter(d => {
                          const logDate = new Date(d);
                          return (
                            logDate.getMonth() === index &&
                            logDate.getFullYear() === currentYear
                          );
                        }).length;
                        return { name, count, isCurrentMonth: index === currentMonth.getMonth() };
                      });

                      const maxCount = Math.max(...monthData.map(m => m.count), 1);

                      return (
                        <div className="space-y-2">
                          {monthData.map((month, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <span className={`text-xs font-medium w-8 ${month.isCurrentMonth ? 'text-primary font-bold' : 'text-muted-foreground'}`}>
                                {month.name}
                              </span>
                              <div className="flex-1 h-6 bg-background/60 rounded-md overflow-hidden relative">
                                <div 
                                  className={`h-full transition-all duration-500 ${
                                    month.isCurrentMonth 
                                      ? 'bg-gradient-to-r from-primary to-accent' 
                                      : 'bg-primary/30'
                                  }`}
                                  style={{ width: `${(month.count / maxCount) * 100}%` }}
                                />
                                <span className="absolute inset-0 flex items-center justify-end pr-2 text-[10px] font-semibold text-foreground">
                                  {month.count > 0 && month.count}
                                </span>
                              </div>
                            </div>
                          ))}
                          <p className="text-[10px] text-muted-foreground/70 mt-2 flex items-center gap-1">
                            <span>📊</span>
                            Current month highlighted in color
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </Card>

              {/* Selected Date Data */}
              <div className="space-y-6">
                <Card className="p-6 bg-gradient-to-br from-primary/10 to-accent/10 border-2 border-primary/20 shadow-elegant rounded-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-foreground">
                      {date?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </h3>
                    <DoctorReport 
                      symptomEpisodes={symptomEpisodes}
                      hotFlashTimes={hotFlashTimes.map(time => ({ time, date: date.toISOString().split('T')[0] }))} 
                      bladderDiaryData={bladderDiaryData}
                      periodLogs={periodLogs}
                      dailyNotes={new Map([[date.toISOString().split('T')[0], dailyNote]])}
                      dailyTriggers={new Map([[date.toISOString().split('T')[0], dailyTriggers]])}
                      symptomTriggers={symptomTriggers}
                      selectedDate={date}
                    />
                  </div>
                  
                  {loading ? (
                    /* Loading Skeletons */
                    <div className="space-y-3 animate-fade-in">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-background/50 rounded-lg backdrop-blur-sm">
                          <div className="flex items-center gap-3">
                            <Skeleton className="h-10 w-10 rounded-full" />
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-24" />
                              <Skeleton className="h-3 w-16" />
                            </div>
                          </div>
                          <Skeleton className="h-6 w-12" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Symptoms with Tabs for selected date */
                    <Tabs className="w-full">
                      <TabsList className="grid w-full grid-cols-6 mb-6 p-1.5 bg-muted/50 rounded-xl border border-border/50 shadow-sm">
                        {symptoms.map((symptom) => {
                          const episodes = symptomEpisodes[symptom.id] || [];
                          return (
                            <TabsTrigger 
                              key={symptom.id} 
                              value={symptom.id} 
                              className="group flex flex-col items-center gap-1.5 py-3 px-2 rounded-lg 
                                data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:border-2 data-[state=active]:border-primary/30 
                                data-[state=active]:scale-105 data-[state=inactive]:hover:scale-102
                                transition-all duration-300 ease-out
                                hover:bg-background/60 hover:shadow-md
                                cursor-pointer"
                            >
                              {renderSymptomIcon(symptom, `h-5 w-5 ${symptom.color} 
                                transition-all duration-300 ease-out
                                group-hover:scale-110 group-hover:-translate-y-0.5
                                group-data-[state=active]:scale-110 group-data-[state=active]:drop-shadow-md
                                group-data-[state=active]:animate-[bounce_0.5s_ease-out]`)} 
                              <span className="text-[10px] font-medium text-muted-foreground 
                                data-[state=active]:text-foreground data-[state=active]:font-semibold
                                transition-all duration-300
                                group-hover:text-foreground/80">
                                {symptom.label}
                              </span>
                              {episodes.length > 0 && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-[8px] text-primary-foreground font-bold rounded-full flex items-center justify-center shadow-md animate-scale-in">
                                  {episodes.length}
                                </span>
                              )}
                            </TabsTrigger>
                          );
                        })}
                      </TabsList>

                      {/* Individual Symptom Tabs */}
                      {symptoms.map((symptom) => {
                        const episodes = symptomEpisodes[symptom.id] || [];
                        
                        return (
                          <TabsContent key={symptom.id} value={symptom.id} className="mt-0">
                            {episodes.length === 0 ? (
                              <div className="text-center py-8">
                                {renderSymptomIcon(symptom, `h-12 w-12 mx-auto mb-3 ${symptom.color} opacity-50`)}
                                <p className="text-sm text-muted-foreground">
                                  No {symptom.label.toLowerCase()} episodes logged for this date
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg backdrop-blur-sm mb-3">
                                  {symptom.image ? (
                                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-primary-soft">
                                      {renderSymptomIcon(symptom, "w-full h-full")}
                                    </div>
                                  ) : (
                                    <div className={`p-2 rounded-full bg-primary-soft ${symptom.color}`}>
                                      {renderSymptomIcon(symptom, "h-5 w-5")}
                                    </div>
                                  )}
                                  <div className="flex-1">
                                    <p className="text-base font-semibold text-foreground">{symptom.label}</p>
                                  </div>
                                </div>
                                {episodes.map((episode) => {
                                  const episodeTriggers = symptomTriggers.get(episode.id || '') || [];
                                  const episodeId = episode.id || '';
                                  
                                  return (
                                    <div key={episode.id} className="p-3 bg-background/30 rounded-lg border border-muted space-y-2">
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                          {symptom.id !== "hydration" && (
                                            <span className="text-xs text-muted-foreground">{episode.time}</span>
                                          )}
                                          <span className={`text-xs font-medium ${getIntensityColor(episode.intensity, symptom.id)}`}>
                                            {getIntensityLabel(episode.intensity, symptom.id)}
                                          </span>
                                          <span className="text-sm font-bold text-primary">{episode.intensity}/10</span>
                                        </div>
                                        
                                        {/* Trigger Dropdown - Hide for hydration */}
                                        {symptom.id !== "hydration" && (
                                          <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 px-2 hover:bg-primary/10"
                                              >
                                                <Tag className="h-3.5 w-3.5 mr-1" />
                                                <span className="text-xs">Trigger</span>
                                                <ChevronDown className="h-3 w-3 ml-1" />
                                              </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent className="w-56 bg-background border-border z-50" align="end">
                                              <div className="p-2 space-y-1">
                                                {triggerOptions.map((trigger) => {
                                                  const TriggerIcon = trigger.icon;
                                                  const isActive = episodeTriggers.includes(trigger.id);
                                                  return (
                                                    <DropdownMenuItem
                                                      key={trigger.id}
                                                      onClick={() => {
                                                        if (episode.id) {
                                                          if (isActive) {
                                                            removeSymptomTrigger(episode.id, trigger.id);
                                                          } else {
                                                            addSymptomTrigger(episode.id, trigger.id);
                                                          }
                                                        }
                                                      }}
                                                      className={`flex items-center gap-2 cursor-pointer ${
                                                        isActive ? 'bg-primary/20 text-primary' : ''
                                                      }`}
                                                    >
                                                      <TriggerIcon className="h-4 w-4" />
                                                      <span>{trigger.label}</span>
                                                      {isActive && <span className="ml-auto text-xs">✓</span>}
                                                    </DropdownMenuItem>
                                                  );
                                                })}
                                                <DropdownMenuSeparator />
                                                <div className="p-2 space-y-2">
                                                  <Input
                                                    placeholder="Custom trigger..."
                                                    value={episodeCustomTriggers[episodeId] || ""}
                                                    onChange={(e) => setEpisodeCustomTriggers(prev => ({ ...prev, [episodeId]: e.target.value }))}
                                                    className="h-8 text-xs"
                                                    onKeyDown={(e) => {
                                                      if (e.key === 'Enter' && episodeCustomTriggers[episodeId]?.trim() && episode.id) {
                                                        const customId = `custom_${episodeCustomTriggers[episodeId].trim().toLowerCase().replace(/\s+/g, '_')}`;
                                                        addSymptomTrigger(episode.id, customId);
                                                        setEpisodeCustomTriggers(prev => ({ ...prev, [episodeId]: "" }));
                                                      }
                                                    }}
                                                  />
                                                  <Button
                                                    size="sm"
                                                    className="w-full h-7 text-xs"
                                                    onClick={() => {
                                                      if (episodeCustomTriggers[episodeId]?.trim() && episode.id) {
                                                        const customId = `custom_${episodeCustomTriggers[episodeId].trim().toLowerCase().replace(/\s+/g, '_')}`;
                                                        addSymptomTrigger(episode.id, customId);
                                                        setEpisodeCustomTriggers(prev => ({ ...prev, [episodeId]: "" }));
                                                      }
                                                    }}
                                                  >
                                                    Add Custom
                                                  </Button>
                                                </div>
                                              </div>
                                            </DropdownMenuContent>
                                          </DropdownMenu>
                                        )}
                                      </div>
                                      
                                      {/* Display active triggers */}
                                      {symptom.id !== "hydration" && episodeTriggers.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-muted/30">
                                          {episodeTriggers.map((triggerId) => {
                                            const trigger = triggerOptions.find(t => t.id === triggerId);
                                            const isCustom = triggerId.startsWith('custom_');
                                            const TriggerIcon = trigger?.icon || Tag;
                                            const label = trigger?.label || (isCustom ? triggerId.replace('custom_', '').replace(/_/g, ' ').charAt(0).toUpperCase() + triggerId.replace('custom_', '').replace(/_/g, ' ').slice(1) : triggerId);
                                            
                                            return (
                                              <button
                                                key={triggerId}
                                                onClick={() => episode.id && removeSymptomTrigger(episode.id, triggerId)}
                                                className="flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded-md text-xs hover:bg-primary/20 transition-colors"
                                              >
                                                <TriggerIcon className="h-3 w-3" />
                                                <span>{label}</span>
                                                <X className="h-3 w-3 ml-0.5 opacity-60 hover:opacity-100" />
                                              </button>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </TabsContent>
                        );
                      })}
                    </Tabs>
                  )}


                  {/* Daily Notes */}
                  {loading ? (
                    <div className="mt-4 pt-4 border-t">
                      <Skeleton className="h-4 w-24 mb-2" />
                      <Skeleton className="h-16 w-full rounded-lg" />
                    </div>
                  ) : dailyNote && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="text-sm font-semibold text-foreground mb-2">Daily Notes</h4>
                      <p className="text-sm text-muted-foreground bg-background/50 p-3 rounded-lg">
                        {dailyNote}
                      </p>
                    </div>
                  )}
                </Card>

                {/* Fluid Balance Chart */}
                <FluidIntakeChart bladderDiaryData={bladderDiaryData} />

                {/* Bladder Diary Calendar View */}
                <BladderDiaryCalendarView 
                  intakeEntries={bladderDiaryData.find(d => d.date === date?.toLocaleDateString())?.intakeEntries || []}
                  urinationEntries={bladderDiaryData.find(d => d.date === date?.toLocaleDateString())?.urinationEntries || []}
                />

                {/* Sleep Log Display */}
                {(() => {
                  const selectedDateStr = date?.toISOString().split('T')[0];
                  const sleepLog = sleepLogs.find(log => log.log_date === selectedDateStr);
                  
                  return (
                    <Card className="p-6 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/20 shadow-elegant rounded-2xl">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-full bg-primary/20">
                          <Moon className="h-5 w-5 text-primary" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground">Sleep Log</h3>
                      </div>
                      
                      {sleepLog ? (
                        <div className="space-y-3">
                          {sleepLog.bedtime && sleepLog.wake_time && (
                            <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                              <span className="text-sm text-muted-foreground">Sleep Duration</span>
                              <span className="text-sm font-semibold text-foreground">
                                {sleepLog.bedtime} - {sleepLog.wake_time}
                                {sleepLog.total_hours && ` (${sleepLog.total_hours}h)`}
                              </span>
                            </div>
                          )}
                          
                          {sleepLog.sleep_quality !== null && (
                            <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                              <span className="text-sm text-muted-foreground">Sleep Quality</span>
                              <span className={`text-sm font-semibold ${getIntensityColor(sleepLog.sleep_quality, 'sleep')}`}>
                                {sleepLog.sleep_quality}/10 - {getIntensityLabel(sleepLog.sleep_quality, 'sleep')}
                              </span>
                            </div>
                          )}
                          
                          {sleepLog.morning_energy !== null && (
                            <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                              <span className="text-sm text-muted-foreground">Morning Energy</span>
                              <span className="text-sm font-semibold text-foreground">
                                {sleepLog.morning_energy}/10
                              </span>
                            </div>
                          )}
                          
                          {sleepLog.times_woken !== null && sleepLog.times_woken > 0 && (
                            <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                              <span className="text-sm text-muted-foreground">Times Woken</span>
                              <span className="text-sm font-semibold text-foreground">
                                {sleepLog.times_woken} times
                              </span>
                            </div>
                          )}
                          
                          {sleepLog.wake_reasons && sleepLog.wake_reasons.length > 0 && (
                            <div className="p-3 bg-background/50 rounded-lg">
                              <span className="text-sm text-muted-foreground mb-2 block">Wake Reasons</span>
                              <div className="flex flex-wrap gap-1.5">
                                {sleepLog.wake_reasons.map((reason: string, idx: number) => (
                                  <span 
                                    key={idx} 
                                    className="px-2 py-1 bg-primary/10 text-primary rounded-md text-xs"
                                  >
                                    {reason}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {sleepLog.room_temp !== null && (
                            <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                              <span className="text-sm text-muted-foreground">Room Temperature</span>
                              <span className="text-sm font-semibold text-foreground">
                                {sleepLog.room_temp}°F
                              </span>
                            </div>
                          )}
                          
                          {sleepLog.what_helped && (
                            <div className="p-3 bg-background/50 rounded-lg">
                              <span className="text-sm text-muted-foreground mb-2 block">What Helped</span>
                              <p className="text-sm text-foreground">{sleepLog.what_helped}</p>
                            </div>
                          )}
                          
                          {sleepLog.notes && (
                            <div className="p-3 bg-background/50 rounded-lg">
                              <span className="text-sm text-muted-foreground mb-2 block">Notes</span>
                              <p className="text-sm text-foreground">{sleepLog.notes}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground bg-background/40 p-4 rounded-lg">
                          No sleep logged for this date yet. Log your night in the sleep tracker to see it here.
                        </p>
                      )}
                      
                      <Link to="/sleep-tracker" className="mt-4 block">
                        <Button variant="outline" size="sm" className="w-full">
                          Open Sleep Tracker
                        </Button>
                      </Link>
                    </Card>
                  );
                })()}

                {/* Insights Card */}
                <Card className="p-6 bg-gradient-to-br from-accent/10 to-primary/10 border-2 border-accent/20 shadow-elegant rounded-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-accent">
                      <TrendingUp className="h-5 w-5" />
                      <h3 className="text-lg font-semibold">Quick Insights</h3>
                    </div>
                    <Link to="/insights">
                      <Button variant="outline" size="sm" className="hover:bg-accent/10">
                        View All
                      </Button>
                    </Link>
                  </div>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <p>📊 Track more days to see personalized patterns</p>
                    <p>💡 Consistent logging helps identify triggers</p>
                  </div>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      </div>
    </div>
  );
};

export default Tracker;
